<template>
  <div :class="$style.content">
    <div
      v-if="loading"
      :class="$style.loading"
    >
      {{ $t('pages.loading') }}
    </div>

    <div
      v-else-if="error"
      :class="$style.error"
    >
      {{ error }}
    </div>

    <template v-else-if="email">
      <div :class="$style.header">
        <div
          v-if="email.from"
          :class="$style.row"
        >
          <span :class="$style.label">{{ $t('pages.from') }}:</span>
          <span>{{ formatAddresses(email.from) }}</span>
        </div>

        <div
          v-if="email.to"
          :class="$style.row"
        >
          <span :class="$style.label">{{ $t('pages.to') }}:</span>
          <span>{{ formatAddresses(email.to) }}</span>
        </div>

        <div
          v-if="email.cc"
          :class="$style.row"
        >
          <span :class="$style.label">{{ $t('pages.copy') }}:</span>
          <span>{{ formatAddresses(email.cc) }}</span>
        </div>

        <div
          v-if="email.date"
          :class="$style.row"
        >
          <span :class="$style.label">{{ $t('global.date') }}:</span>
          <span>{{ email.date }}</span>
        </div>

        <div
          v-if="email.subject"
          :class="$style.row"
        >
          <span :class="$style.label">{{ $t('pages.subject') }}:</span>
          <span>{{ email.subject }}</span>
        </div>
      </div>

      <iframe
        v-if="email.html"
        :class="$style.iframe"
        :srcdoc="email.html"
        sandbox
        referrerpolicy="no-referrer"
        title="Email content"
      />

      <pre
        v-else-if="email.text"
        :class="$style.text"
      >{{ email.text }}</pre>

      <div
        v-if="email.attachments && email.attachments.length"
        :class="$style.attachments"
      >
        <div :class="$style.attachmentsTitle">
          {{ $t('pages.attachments') }}
        </div>

        <a
          v-for="(attachment, index) in email.attachments"
          :key="index"
          :href="attachment.url"
          :download="attachment.filename"
          :class="$style.attachment"
        >
          {{ attachment.filename }}
        </a>
      </div>
    </template>
  </div>
</template>

<script>
  import PostalMime from 'postal-mime';
  import MsgReader from '@kenjiuno/msgreader';
  import { downloadMail } from '@/api/methods/computers';
  import { dateTime } from '@/helpers/filters';
  import { getFilename, getExtension, escapeRegExp } from '../utils';

  export default {
    props: {
      computerId: {
        type: [Number, String],
        required: true,
      },
      recordId: {
        type: [Number, String],
        required: true,
      },
    },
    data() {
      return {
        loading: false,
        error: null,
        email: null,
        attachmentUrls: [],
      };
    },
    watch: {
      recordId: {
        handler(newVal) {
          if (newVal) {
            this.loadEmail();
          }
        },
        immediate: true,
      },
    },
    beforeDestroy() {
      this.revokeAttachmentUrls();
    },
    methods: {
      async loadEmail() {
        this.loading = true;
        this.error = null;

        try {
          const response = await downloadMail(this.computerId, this.recordId);

          const filename = getFilename(response.headers);
          const extension = getExtension(filename);

          if (extension === 'msg') {
            await this.parseMsg(response.data);
          } else {
            await this.parseEml(response.data);
          }
        } catch (error) {
          console.error('Email loading error:', error);

          if (error.response?.status === 404) {
            this.error = this.$t('toast.emailNotFound');
          } else {
            this.error = this.$t('toast.emailLoadError');
          }
        } finally {
          this.loading = false;
        }
      },

      async parseEml(blob) {
        const parsed = await PostalMime.parse(blob);

        const dateHeader = parsed.headers.find(({ key }) => key === 'date');

        this.email = {
          subject: parsed.subject || '',
          from: parsed.from,
          to: parsed.to,
          cc: parsed.cc,
          date: dateHeader?.value,
          html: this.replaceCidImages(parsed.html || '', parsed.attachments || []),
          text: parsed.text || '',
          attachments: this.prepareAttachments(parsed.attachments || []),
        };
      },

      async parseMsg(blob) {
        const buffer = await blob.arrayBuffer();
        const msgReader = new MsgReader(buffer);
        const data = msgReader.getFileData();

        const attachments = this.prepareMsgAttachments(data.attachments || []);

        this.email = {
          subject: data.subject || '',
          from: this.mapMsgSender(data),
          to: this.mapMsgRecipients(data.recipients),
          cc: this.mapMsgRecipients((data.recipients || []).filter((recipient) => recipient.recipType === 2)),
          date: dateTime(data.messageDeliveryTime),
          html: data.bodyHtml || '',
          text: data.body || '',
          attachments,
        };
      },

      prepareAttachments(attachments) {
        return attachments.map((attachment) => {
          const content = this.toUint8Array(attachment.content);

          const blob = new Blob([content], {
            type: attachment.mimeType || 'application/octet-stream',
          });

          const url = URL.createObjectURL(blob);

          this.attachmentUrls.push(url);

          return {
            filename: attachment.filename || 'attachment',
            mimeType: attachment.mimeType,
            content,
            contentId: attachment.contentId,
            url,
          };
        });
      },

      prepareMsgAttachments(attachments) {
        return attachments.map((attachment) => {
          const content = this.toUint8Array(attachment.content);

          const blob = new Blob([content], {
            type: attachment.mimeType || 'application/octet-stream',
          });

          const url = URL.createObjectURL(blob);

          this.attachmentUrls.push(url);

          return {
            filename: attachment.fileName || attachment.name || 'attachment',
            mimeType: attachment.mimeType,
            content,
            contentId: attachment.contentId,
            url,
          };
        });
      },

      replaceCidImages(html, attachments) {
        if (!html || !attachments.length) {
          return html;
        }

        let result = html;

        attachments.forEach((attachment) => {
          if (!attachment.contentId || !attachment.content) {
            return;
          }

          const content = this.toUint8Array(attachment.content);
          const base64 = this.uint8ArrayToBase64(content);
          const mimeType = attachment.mimeType || 'application/octet-stream';

          const dataUrl = `data:${mimeType};base64,${base64}`;

          const contentId = attachment.contentId.replace(/^<|>$/g, '');

          const escapedContentId = escapeRegExp(contentId);

          result = result.replace(new RegExp(`cid:<${escapedContentId}>|cid:${escapedContentId}`, 'gi'), dataUrl);
        });

        return result;
      },

      toUint8Array(content) {
        if (content instanceof Uint8Array) {
          return content;
        }

        if (content instanceof ArrayBuffer) {
          return new Uint8Array(content);
        }

        return new Uint8Array(content);
      },

      uint8ArrayToBase64(bytes) {
        let binary = '';
        const chunkSize = 0x8000;

        for (let i = 0; i < bytes.length; i += chunkSize) {
          const chunk = bytes.subarray(i, Math.min(i + chunkSize, bytes.length));

          binary += String.fromCharCode(...chunk);
        }

        return btoa(binary);
      },

      mapMsgSender(data) {
        if (data.senderName || data.senderEmail) {
          return {
            name: data.senderName,
            address: data.senderEmail,
          };
        }

        return null;
      },

      mapMsgRecipients(recipients) {
        if (!recipients || !recipients.length) {
          return [];
        }

        return recipients.map((recipient) => ({
          name: recipient.name,
          address: recipient.email || recipient.smtpAddress || recipient.emailAddress,
        }));
      },

      formatAddresses(addresses) {
        if (!addresses) {
          return '';
        }

        if (!Array.isArray(addresses)) {
          addresses = [addresses];
        }

        return addresses
          .map((address) => this.formatAddress(address))
          .filter(Boolean)
          .join(', ');
      },

      formatAddress(address) {
        if (!address) {
          return '';
        }

        if (typeof address === 'string') {
          return address;
        }

        if (address.name && address.address) {
          return `${address.name} <${address.address}>`;
        }

        return address.address || address.name || '';
      },
      revokeAttachmentUrls() {
        this.attachmentUrls.forEach((url) => {
          URL.revokeObjectURL(url);
        });

        this.attachmentUrls = [];
      },
      close() {
        this.$emit('close');
      },
    },
  };
</script>

<style lang="scss" module>
  .content {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .loading,
  .error {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
  }

  .error {
    color: #d32f2f;
  }

  .header {
    flex: 0 0 auto;
    padding: 16px 20px;
    border-bottom: 1px solid #e5e5e5;
  }

  .row {
    display: flex;
    margin-bottom: 8px;
    line-height: 20px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .label {
    flex: 0 0 70px;
    font-weight: 600;
  }

  .iframe {
    flex: 1;
    width: 100%;
    border: 0;
    background: #fff;
  }

  .text {
    flex: 1;
    margin: 0;
    padding: 20px;
    overflow: auto;
    white-space: pre-wrap;
    word-break: break-word;
    font-family: inherit;
  }

  .attachments {
    flex: 0 0 auto;
    padding: 12px 20px;
    border-top: 1px solid #e5e5e5;
  }

  .attachmentsTitle {
    margin-bottom: 8px;
    font-weight: 600;
  }

  .attachment {
    display: block;
    margin-bottom: 4px;
  }
</style>
